import { useCallback, useEffect, useState } from "react";
import { Button, Modal as BootstrapModal } from "react-bootstrap";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

import Auth from "../../../services/auth";
import Domain from "../../../services/domain";
import { useNavigate } from "react-router-dom";
import RadioButtonList from "../../../components/controls/form/RadioButtonList";
import { useDeploymentStages } from "../../../common/hooks/Hooks";
import Utils from "../../../utils/utils";
import Product from "../../../services/product";
import { useAppContext } from "../../../common/ApplicationServicesContext";
import ROUTES from "../../../common/routes";

const ChangeDomainModal = ({ show, cancelText, onCancel = () => { }, redirect }) => {
    const [domains, setDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState("");
    const { domain: currentDomain, setDomain: setCurrentDomain, setIsRootDomain } = useAppContext();
    const { clearDeploymentStages } = useDeploymentStages();
    const navigate = useNavigate();
    const [translate] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const populateDomains = useCallback(async () => {
        setIsLoading(true);
        const response = await Domain.getAllDomains({
            showDisabled: false
        });
        if (response && !response.error) {
            const rootDomain = response.data.find(domain => domain.rootDomain);
            const filteredDomains = response.data.filter(domain => !domain.rootDomain);
            const sorted = rootDomain ? [rootDomain, ...filteredDomains] : [...filteredDomains];
            setDomains(sorted);
            if (currentDomain) {
                setSelectedDomain(currentDomain._id);
            }
        }
        setIsLoading(false);
    }, [currentDomain]);

    useEffect(() => {
        if (show) {
            populateDomains();
        }
    }, [show, populateDomains]);

    const redirectToPage = useCallback((selectedDomainObj) => {
        if (!redirect) {
            navigate("/");
            return
        }
        const firstPath = redirect.split('/')[1];
        const deploymentId = redirect.split('/')[2];

        if (([ROUTES.ENVIRONMENTS, ROUTES.DOMAIN_CONFIGURATION, ROUTES.EMAIL_MANAGEMENT, ROUTES.SITE_MONITORING].includes(firstPath) || deploymentId)
            && selectedDomainObj.rootDomain) {
            // do not use redirect, route is not defined on rootDomain
            navigate("/");
            return;
        }

        navigate(redirect);
    }, [redirect, navigate]);

    const setDomainCb = useCallback(async () => {
        const selectedDomainObj = domains.find(d => d._id === selectedDomain);
        if (!selectedDomainObj) return;
        setIsRootDomain(selectedDomainObj.rootDomain || false);

        const [promptInfoResponse, switchResponse] = await Promise.all([
            Product.getDomainPromptInfo(selectedDomain),
            Auth.switchDomain(selectedDomain)
        ]);

        if (switchResponse?.error) {
            return;
        }

        const { helpUrl, termsAndConditionsUrl } = promptInfoResponse.data;
        setCurrentDomain({
            _id: selectedDomain,
            name: selectedDomainObj.name,
            helpUrl,
            termsAndConditionsUrl,
            enabled: selectedDomainObj.enabled,
            trainingUrls: selectedDomainObj.trainingUrls ?? [],
            trainingText: selectedDomainObj.trainingText,
            acceptedTermsAndConditionsUrl: selectedDomainObj.acceptedTermsAndConditionsUrl,
        });
        clearDeploymentStages();
        redirectToPage(selectedDomainObj);
        window.location.reload();
    }, [domains, selectedDomain, setCurrentDomain, setIsRootDomain, clearDeploymentStages, redirectToPage]);

    useEffect(() => {
        if (!show || !selectedDomain) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onCancel();
            }
            if (e.key === "Enter") {
                setDomainCb();
            }
        }
        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, setDomainCb, show, selectedDomain])

    const updateSelection = (selected) => {
        setSelectedDomain(selected);
    };

    return <Formik
        key={show ? "modal-open" : "modal-closed"}
        initialValues={{
            domain: [...domains]
        }}
        onSubmit={setDomainCb}
        enableReinitialize
    >
        <BootstrapModal show={show} backdrop="static" scrollable>
            <BootstrapModal.Header className="h5 text-secondary">
                {translate("OTM_SELECT_DOMAIN")}
            </BootstrapModal.Header>
            <BootstrapModal.Body>
                <Form id="changeDomain" className="d-flex flex-column overflow-hidden">
                    {!isLoading && domains?.length === 0 && <h6>{translate("OTM_NO_DOMAIN_ACCESS")}</h6>}

                    {domains?.length > 0 && <RadioButtonList
                        name="domain"
                        heading={translate("OTM_SELECT_A_DOMAIN")}
                        onSelect={updateSelection}
                        labelStylingCb={(el) => { return el.enabled ? '' : 'text-danger' }}
                    />}
                </Form>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <Button
                    size="sm"
                    variant="primary"
                    type="submit"
                    form="changeDomain"
                    disabled={Utils.isEmpty(selectedDomain) || selectedDomain === currentDomain._id}
                >
                    {translate("OTM_SELECT")}
                </Button>
                <Button
                    size="sm"
                    onClick={onCancel}
                    variant="secondary"
                >
                    {translate(cancelText)}
                </Button>
            </BootstrapModal.Footer>
        </BootstrapModal>
    </Formik>
};

export default ChangeDomainModal;
